import {
  Component,
  HostBinding,
  Input,
  signal,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { BaseWidget } from '../base-widget/base-widget';
import {
  ActionEnum,
  ActionRequestDto,
} from '@app/core/dto/abstract-action.dto';
import { StringUtils } from '@app/core/utils/string.utils';
import { VerificationTypeEnum } from '@app/core/enums/verification-type.enum';
import { GlobalValidator } from '@app/core';
interface AddressBookData {
  isDataValid: boolean;
  isPincodeValid: boolean;
  address: string;
  city_id: number;
  state_id: number;
  country_id: number;
  landmark: string;
  pincode: string;
  type: string;
  phone: string;
  email: string;
  default: boolean;
  first_name: string;
  last_name: string;
  company_name: string;
  name: string;
  tin_number: string | null;
  gstin_number: string;
  alternate_phone: string | null;
  address2: string;
  check_cibil: boolean;
  created_on: string;
  modified: string;
  used_on: string;
  address_type: string;
  deleted_at: string | null;
  gstin_verified: boolean;
  city: { id: number; name: string };
  state: {
    id: number;
    name: string;
    type: string;
    code: string;
    is_ib_credit_eligible: boolean;
  };
  country: { id: number; name: string };
}

@Component({
  selector: 'user-quote',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './user-quote.component.html',
  styleUrl: './user-quote.component.scss',
})
export class UserQuoteComponent extends BaseWidget {
  fillQuote: WritableSignal<boolean> = signal(true);
  userQuoteForm: FormGroup;
  quoteItemForm: FormGroup;
  addressBookData = signal<AddressBookData | null>(null);
  quotation = signal<any[]>([]);
  tableRowCount = signal(1);
  tableRows = signal<any[]>([{ id: 1 }]);
  brand = signal('');
  description = signal('');
  quantity = signal('');
  fileName = signal('');
  fileUploadStatus = signal('');
  fileUploadType = signal([]);
  maxDocSize = signal(0);
  maxQuoteItem = signal(0);
  disabledAddButton = signal(false);
  BYTES_PER_MB: number = 1024 * 1024;
  StringUtils = StringUtils;
  actionButtons: any;
  invalidForm = signal('');
  fetchPincode = signal(false);
  invalidPincode = signal(false);
  constants = signal(null);
  showAgentId = signal(false);

  @HostBinding('class') @Input() ngClass: any;
  @HostBinding('style') @Input() ngStyle: any;

  @Input() actionEvent: (event: any) => void;
  @Input() slot: any;

  constructor(
    private formBuilder: FormBuilder,
  ) {
    super();
  }
  ngOnInit(): void {
    this.addressBookData.set(this.slot?.widget?.data?.addressBook);
    this.quotation.set(this.slot?.widget?.data?.quotation);
    this.actionButtons = this.slot?.widget?.data?.actionButtons;

    this.quoteFormBuilder();
    if (this.addressBookData()?.phone) {
      this.patchDetails();
    }

    this.maxDocSize.set(this.slot?.widget?.data?.constants?.fileUploadSize);
    this.maxQuoteItem.set(this.slot?.widget?.data?.constants?.maxQuoteItem);
    this.fileUploadType.set(this.slot.widget.data.constants.fileUploadType);
    this.constants.set(this.slot.widget.data.constants);
  }

  quoteFormBuilder() {
    this.userQuoteForm = this.formBuilder.group({
      name: [
        '',
        [
          Validators.required,
          Validators.pattern(GlobalValidator.NAME_REGEX),
          GlobalValidator.minLengthTrimmed(3),
          GlobalValidator.maxLengthTrimmed(100),
        ],
      ],
      mobile: [
        '',
        [Validators.required, Validators.pattern(GlobalValidator.PHONE_REGEX)],
      ],
      email: [
        '',
        [Validators.required, Validators.pattern(GlobalValidator.EMAIL_REGEXP)],
      ],
      company: [
        '',
        [Validators.required, GlobalValidator.maxLengthTrimmed(50)],
      ],
      gst: ['', [Validators.pattern(GlobalValidator.GSTIN_REGEX)]],
      address: [
        '',
        [
          GlobalValidator.minLengthTrimmed(10),
          GlobalValidator.maxLengthTrimmed(200),
          Validators.pattern(GlobalValidator.ADDRESS_REGEX),
        ],
      ],
      landmark: [
        '',
        [
          GlobalValidator.minLengthTrimmed(5),
          GlobalValidator.maxLengthTrimmed(30),
          Validators.pattern(GlobalValidator.ADDRESS_REGEX),
        ],
      ],
      zipcode: [
        '',
        [
          Validators.required,
          Validators.pattern(GlobalValidator.PINCODE_REGEX),
        ],
      ],
      state: ['', [Validators.required, Validators.maxLength(20)]],
      city: ['', [Validators.required, Validators.maxLength(20)]],
      quotationItems: this.formBuilder.array([this.createQuoteItem()]),
      uploadedQuote: [''],
      agentId: [],
    });
  }

  createQuoteItem(): FormGroup {
    return this.formBuilder.group({
      brand: ['', [Validators.maxLength(100)]],
      description: ['', [Validators.required, Validators.maxLength(200)]],
      quantity: [
        '',
        [Validators.required, Validators.pattern(GlobalValidator.NUMBERS_ONLY)],
      ],
      targetPrice: [''],
    });
  }

  changeTab(flag: boolean) {
    this.fillQuote.set(flag);

    const quotationItemsArray: FormArray<any> = this.userQuoteForm?.get(
      'quotationItems',
    ) as FormArray;
    const uploadedQuoteControl: AbstractControl<any, any> =
      this.userQuoteForm?.get('uploadedQuote');

    if (!this.fillQuote()) {
      // Clear the FormArray and any validators if needed
      quotationItemsArray.clear();
      uploadedQuoteControl?.setValidators([Validators.required]);
      uploadedQuoteControl?.updateValueAndValidity();
    } else {
      // Clear existing items and add a new FormGroup
      quotationItemsArray.clear();
      quotationItemsArray.push(this.createQuoteItem());

      uploadedQuoteControl?.clearValidators();
      uploadedQuoteControl?.updateValueAndValidity();
    }
  }

  handleSubmit() {
    if (!this.userQuoteForm?.valid) {
      this.invalidForm.set(this.slot?.widget?.data?.constants?.invalidForm);
      return;
    }

    if (!this.fillQuote()) {
      if (!this.userQuoteForm.get('uploadedQuote')?.value.length) {
        this.fileUploadStatus.set('Please upload quote!');
        return;
      }
    }
    if (this.userQuoteForm.get('uploadedQuote')?.value.length) {
      if (this.fillQuote()) {
        this.userQuoteForm.patchValue({
          uploadedQuote: '',
        });
      }
    }
    let params: Record<string, any> = {
      ...this.userQuoteForm.getRawValue(),
    };
    params['uploadType'] = this.fillQuote() ? 'FILLED_QUOTE' : 'TEMPLATE_QUOTE';
    this.actionEvent({
      actionData: {
        type: ActionEnum.SUBMIT_USER_QUOTE,
        params,
      },
    });
    this.scrollToTop();
  }

  patchDetails() {
    const data: AddressBookData = this.addressBookData();
    if (data) {
      const patchedFields: string[] = [
        'name',
        'mobile',
        'email',
        'company',
        'gst',
        'address',
        'landmark',
        'zipcode',
        'state',
        'city',
      ];
      this.userQuoteForm.patchValue({
        name: StringUtils.format('{0} {1}', data?.first_name, data?.last_name),
        mobile: data?.phone,
        email: data?.email,
        company: data?.company_name,
        gst: data?.gstin_number,
        address: StringUtils.format('{0} {1}', data?.address, data?.address2),
        landmark: data?.landmark,
        zipcode: data?.pincode,
        state: data?.state?.name,
        city: data?.city?.name,
        country: data?.country?.name,
      });
      // Surface validation errors for any prefilled value that fails the
      // newly-added validators instead of leaving the form silently invalid.
      patchedFields.forEach((field) =>
        this.userQuoteForm.get(field)?.markAsTouched(),
      );
    }

    if (data?.state?.name && data?.city?.name) {
      this.userQuoteForm.get('state')?.disable();
      this.userQuoteForm.get('city')?.disable();
    }
  }

  get quotationItems(): FormArray {
    return this.userQuoteForm?.get('quotationItems') as FormArray;
  }

  addTableRow(): void {
    // Prevent adding an empty row if the last row is already empty
    if (this.quotationItems.length >= this.maxQuoteItem()) {
      return;
    }
    const lastItem: Record<string, any> = this.quotationItems?.at(
      this.quotationItems?.length - 1,
    )?.value;
    if (!lastItem?.quantity?.length || !lastItem?.description?.length) {
      this.disabledAddButton.set(true);
      // Mark the last row fields as touched to show validation errors
      const lastFormGroup: FormGroup = this.quotationItems?.at(
        this?.quotationItems?.length - 1,
      ) as FormGroup;
      Object?.keys(lastFormGroup?.controls)?.forEach((key) => {
        lastFormGroup?.get(key)?.markAsTouched();
      });
      return;
    }
    this.disabledAddButton.set(false);

    this.quotationItems.push(this.createQuoteItem());

    setTimeout(() => {
      const scrollContainer: Element =
        document.querySelector('.overflow-y-auto');
      if (scrollContainer) {
        scrollContainer?.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 100);
  }

  deleteTableRow(index: number): void {
    if (this.quotationItems.length === 1) {
      return;
    }
    this.quotationItems.removeAt(index);

    this.quotationItems.controls.forEach((control, i) => {
      control.get('id')?.setValue(i + 1);
    });
    const lastItem: Record<string, any> = this.quotationItems.at(
      this.quotationItems.length - 1,
    )?.value;
    if (!lastItem?.quantity?.length || !lastItem?.description?.length) {
      this.disabledAddButton.set(true);
      return;
    }
    this.disabledAddButton.set(false);
  }

  uploadFile(event: any) {
    const file: File = event.target.files[0];

    if (!this.isValidFileSize(file)) {
      this.fileUploadStatus.set(
        this.slot?.widget?.data?.constants?.invalidFileUploadSize,
      );
      return;
    }
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    this.apiService.callFileUploadApi(formData).subscribe({
      next: (res) => {
        if (res.status) {
          this.userQuoteForm.patchValue({
            uploadedQuote: res.data.publicUrl,
          });
          this.fileName.set(file.name);
        } else {
          this.fileUploadStatus.set('upload failed');
        }
      },
      error: (error) => {
        this.fileUploadStatus.set('upload failed');
      },
    });
  }

  isValidFileSize(file: File): boolean {
    const fileSizeInMB: number = file.size / this.BYTES_PER_MB;

    if (fileSizeInMB > this.maxDocSize()) {
      return false;
    }
    return true;
  }

  performAction(action: ActionRequestDto) {
    this.actionEvent({
      actionData: {
        ...action,
      },
    });
  }

  redirectToQuote() {
    window.location.reload();
  }

  validateInput(event: any, type: 'quantity' | 'price', itemIndex: number) {
    let inputValue: string = event.target.value;

    if (type === 'quantity') {
      inputValue = inputValue.replace(/[^0-9]/g, ''); // Allow only numbers
    } else if (type === 'price') {
      inputValue = inputValue.replace(/^(\d*\.?\d{0,2}).*$/, '$1'); // Allow numbers & dot
    }

    // Update DOM element
    event.target.value = inputValue;

    // Update form control value
    const controlName = type === 'quantity' ? 'quantity' : 'targetPrice';
    const quotationItemsArray = this.userQuoteForm.get(
      'quotationItems',
    ) as FormArray;
    const itemFormGroup = quotationItemsArray.at(itemIndex) as FormGroup;
    const control = itemFormGroup.get(controlName);

    if (control) {
      control.setValue(inputValue, { emitEvent: false });
    }
  }

  checkPincodeDetails() {
    this.fetchPincode.set(true);
    const pincode: AbstractControl<any, any> =
      this.userQuoteForm?.get('zipcode');
    if (!pincode.valid) {
      this.fetchPincode.set(false);
      return;
    }

    const body: {
      type: VerificationTypeEnum;
      pincode: any;
    } = {
      type: VerificationTypeEnum.PINCODE,
      pincode: pincode.value,
    };

    this.apiService.callVerificationApi(body).subscribe({
      next: (res) => {
        const response: Record<string, any> = res.RESPONSE.data;
        if (!response.status) {
          this.userQuoteForm.patchValue({
            pincode: pincode.value,
          });
          this.invalidPincode.set(true);
          return;
        }

        this.userQuoteForm.patchValue({
          city: response?.city?.name,
          state: response?.state?.name,
        });
        this.userQuoteForm.get('state')?.disable();
        this.userQuoteForm.get('city')?.disable();
        this.fetchPincode.set(false);
        this.invalidPincode.set(false);
      },
      error: () => {
        this.fetchPincode.set(false);
      },
    });
  }
  scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  captureAgentId(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    const isChecked: boolean = target.checked;
    const agentIdControl: AbstractControl<any, any> =
      this.userQuoteForm.get('agentId');

    if (!agentIdControl) return;

    this.showAgentId.set(isChecked);

    if (isChecked) {
      agentIdControl.setValidators([
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(GlobalValidator.IB_MAIL_REGEX),
      ]);
    } else {
      agentIdControl.setValue(null);
      agentIdControl.clearValidators();
    }

    agentIdControl.updateValueAndValidity();
  }
}
